/**
 * Goals API client
 *
 * Provides methods for interacting with the Goal Manager API.
 * Goals represent desired outcomes that commitments and tasks contribute to.
 */

import { fetchApi, buildQueryString } from './client';
import type {
  Goal,
  GoalListResponse,
  GoalTreeResponse,
  GoalStatsResponse,
  GoalFilters,
  CreateGoalInput,
  UpdateGoalInput,
  GoalRelationshipType,
  AddRelationshipInput,
  RelatedGoalsResponse,
} from '@/types';

// Goals API base path - relative to API_BASE which is /api
const GOALS_API_BASE = import.meta.env.VITE_GOALS_API_BASE ?? '/goals';

/**
 * List goals with optional filters
 */
export async function listGoals(
  filters?: GoalFilters,
  options?: {
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }
): Promise<GoalListResponse> {
  const queryParams = {
    ...filters,
    limit: options?.limit,
    offset: options?.offset,
    sort_by: options?.sortBy,
    sort_order: options?.sortOrder,
  };

  const query = buildQueryString(queryParams as Record<string, string | number | boolean | undefined>);
  return fetchApi<GoalListResponse>(`${GOALS_API_BASE}${query}`);
}

/**
 * Get a single goal by ID
 */
export async function getGoal(goalId: string): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}`);
}

/**
 * Get goals as a hierarchical tree
 */
export async function getGoalTree(rootGoalId?: string): Promise<GoalTreeResponse> {
  const query = rootGoalId ? `?root=${rootGoalId}` : '';
  return fetchApi<GoalTreeResponse>(`${GOALS_API_BASE}/tree${query}`);
}

/**
 * Get goal statistics
 */
export async function getGoalStats(): Promise<GoalStatsResponse> {
  return fetchApi<GoalStatsResponse>(`${GOALS_API_BASE}/stats`);
}

/**
 * Create a new goal
 */
export async function createGoal(input: CreateGoalInput): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(GOALS_API_BASE, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update a goal
 */
export async function updateGoal(
  goalId: string,
  input: UpdateGoalInput
): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a goal (soft delete)
 */
export async function deleteGoal(goalId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`${GOALS_API_BASE}/${goalId}`, {
    method: 'DELETE',
  });
}

/**
 * Mark a goal as complete
 */
export async function completeGoal(goalId: string): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}/complete`, {
    method: 'POST',
  });
}

/**
 * Reopen a completed goal
 */
export async function reopenGoal(goalId: string): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}/reopen`, {
    method: 'POST',
  });
}

/**
 * Update goal progress
 */
export async function updateGoalProgress(
  goalId: string,
  progress: number
): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}/progress`, {
    method: 'PUT',
    body: JSON.stringify({ progress }),
  });
}

/**
 * Get related goals for a specific goal
 */
export async function getRelatedGoals(
  goalId: string,
  relationshipType?: GoalRelationshipType
): Promise<RelatedGoalsResponse> {
  const query = relationshipType ? `?type=${relationshipType}` : '';
  return fetchApi<RelatedGoalsResponse>(`${GOALS_API_BASE}/${goalId}/related${query}`);
}

/**
 * Add a relationship between goals
 */
export async function addGoalRelationship(
  goalId: string,
  input: AddRelationshipInput
): Promise<{ goal: Goal }> {
  return fetchApi<{ goal: Goal }>(`${GOALS_API_BASE}/${goalId}/relationships`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Remove a relationship between goals
 */
export async function removeGoalRelationship(
  goalId: string,
  relatedGoalId: string,
  options?: {
    type?: GoalRelationshipType;
    bidirectional?: boolean;
  }
): Promise<{ goal: Goal }> {
  const params: Record<string, string> = {};
  if (options?.type) params.type = options.type;
  if (options?.bidirectional !== undefined) params.bidirectional = String(options.bidirectional);
  
  const query = buildQueryString(params);
  return fetchApi<{ goal: Goal }>(
    `${GOALS_API_BASE}/${goalId}/relationships/${relatedGoalId}${query}`,
    { method: 'DELETE' }
  );
}

/**
 * Get goals aligned with a specific value
 */
export async function getGoalsByValue(valueId: string): Promise<GoalListResponse> {
  return listGoals({ valueIds: valueId });
}

/**
 * Get active goals only
 */
export async function getActiveGoals(): Promise<GoalListResponse> {
  return listGoals({ status: 'active' });
}

/**
 * Get parent goals (goals that this goal is a sub-goal of)
 */
export async function getParentGoals(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'parent');
}

/**
 * Get child goals (goals that are sub-goals of this goal)
 */
export async function getChildGoals(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'child');
}

/**
 * Get blocking goals (goals that this goal blocks)
 */
export async function getBlockingGoals(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'blocks');
}

/**
 * Get goals that block this goal
 */
export async function getBlockedByGoals(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'blocked_by');
}

/**
 * Get dependencies (goals this goal depends on)
 */
export async function getDependencies(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'depends_on');
}

/**
 * Get dependents (goals that depend on this goal)
 */
export async function getDependents(goalId: string): Promise<RelatedGoalsResponse> {
  return getRelatedGoals(goalId, 'dependency_of');
}

// =============================================================================
// Supporting Tasks (Tasks that advance/support a goal)
// =============================================================================

/**
 * Supporting task info returned from goal API
 */
export interface SupportingTask {
  taskId: string;
  strength?: number;
  source?: string;
  createdAt?: string;
}

/**
 * Response for goal supporting tasks query
 */
export interface GoalTasksResponse {
  goalId: string;
  taskCount: number;
  tasks: SupportingTask[];
}

/**
 * Get tasks that support/advance a goal
 */
export async function getGoalTasks(goalId: string): Promise<GoalTasksResponse> {
  return fetchApi<GoalTasksResponse>(`${GOALS_API_BASE}/${goalId}/tasks`);
}

/**
 * Remove a task's association with a goal
 */
export async function removeGoalTask(
  goalId: string,
  taskId: string
): Promise<{ success: boolean; taskId: string; goalId: string }> {
  return fetchApi<{ success: boolean; taskId: string; goalId: string }>(
    `${GOALS_API_BASE}/${goalId}/tasks/${taskId}`,
    { method: 'DELETE' }
  );
}

